import { HStack, Input, Spinner, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdCheckCircle, MdErrorOutline } from "react-icons/md";
import { ApiDocType, serverService } from "services/serverService";

/**
 * idle — the field matches the stored URL, so there is nothing to check.
 * checking / valid / invalid — outcome of validating a changed URL.
 */
export type SpecUrlValidationState = "idle" | "checking" | "valid" | "invalid";

interface Status {
  state: SpecUrlValidationState;
  message: string;
  wasResolved: boolean;
}

const IDLE: Status = { state: "idle", message: "", wasResolved: false };

interface SpecUrlFieldProps {
  /** Current field value. */
  value: string;
  /** URL currently stored for the toolkit — no validation runs while the value equals it. */
  storedValue: string;
  /** Documentation format the toolkit was generated with; the URL is validated against it. */
  apiDocType: ApiDocType;
  onChange: (specUrl: string) => void;
  onValidationStateChange: (state: SpecUrlValidationState) => void;
}

const DEBOUNCE_MS = 600;

/**
 * Specification URL input that validates a changed URL against the live API as the admin types,
 * so a bad address is caught here rather than on the next "Check for Updates".
 */
export const SpecUrlField = ({
  value,
  storedValue,
  apiDocType,
  onChange,
  onValidationStateChange,
}: SpecUrlFieldProps) => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<Status>(IDLE);

  // Mirrors `status` so a repeated transition — every keystroke re-enters "checking" — issues no
  // setState at all. Without the guard the field schedules an update from a passive effect on each
  // keystroke, the root never clears its sync lanes while typing, and React trips its
  // nested-update limit with "Maximum update depth exceeded".
  const statusRef = useRef<Status>(IDLE);
  // Last URL that came back valid, with the status it produced — lets the effect skip a second
  // fetch after a documentation page is swapped for the spec URL resolved from it.
  const validatedRef = useRef<{ url: string; status: Status } | null>(null);
  // Guards against a slow earlier response overwriting the result of a later one.
  const requestIdRef = useRef(0);

  const applyStatus = useCallback(
    (next: Status) => {
      const prev = statusRef.current;
      if (
        prev.state === next.state &&
        prev.message === next.message &&
        prev.wasResolved === next.wasResolved
      ) {
        return;
      }
      statusRef.current = next;
      setStatus(next);
      if (prev.state !== next.state) {
        onValidationStateChange(next.state);
      }
    },
    [onValidationStateChange],
  );

  useEffect(() => {
    const trimmed = value.trim();

    if (trimmed === storedValue.trim()) {
      applyStatus(IDLE);
      return;
    }

    if (!trimmed) {
      applyStatus({
        state: "invalid",
        message: t("app.servers.specUrlRequired"),
        wasResolved: false,
      });
      return;
    }

    if (validatedRef.current && trimmed === validatedRef.current.url) {
      applyStatus(validatedRef.current.status);
      return;
    }

    applyStatus({ state: "checking", message: "", wasResolved: false });

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const response = await serverService.validateSpec({
          specUrl: trimmed,
          apiDocType,
        });

        if (requestId !== requestIdRef.current) return;

        if (!response.isValid) {
          applyStatus({
            state: "invalid",
            message: response.message || t("app.servers.specUrlInvalid"),
            wasResolved: false,
          });
          return;
        }

        // A documentation page was pasted rather than the document itself — show the spec URL we
        // actually read, so what gets saved matches what the admin sees.
        const effectiveUrl =
          response.specUrlWasResolved && response.resolvedSpecUrl
            ? response.resolvedSpecUrl
            : trimmed;

        const validStatus: Status = {
          state: "valid",
          message: response.estimatedToolCount
            ? t("app.servers.specUrlValid", {
                endpoints: response.estimatedToolCount,
              })
            : t("app.servers.specUrlValidNoCount"),
          wasResolved: response.specUrlWasResolved === true,
        };

        validatedRef.current = { url: effectiveUrl, status: validStatus };
        applyStatus(validStatus);

        if (effectiveUrl !== trimmed) {
          onChange(effectiveUrl);
        }
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;

        applyStatus({
          state: "invalid",
          message:
            err.response?.data?.message ||
            err.response?.data?.error ||
            t("app.servers.specUrlInvalid"),
          wasResolved: false,
        });
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value, storedValue, apiDocType, applyStatus, onChange, t]);

  return (
    <VStack align="stretch" gap={1}>
      <Text fontSize="sm" fontWeight={500} mb={1} color="var(--text-primary)">
        {t("app.servers.specUrl")}
      </Text>
      <Input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("app.servers.specUrlPlaceholder")}
        h="40px"
      />
      <Text fontSize="xs" color="var(--text-secondary-color)">
        {t("app.servers.specUrlHelper")}
      </Text>

      {status.state === "checking" && (
        <HStack gap={2}>
          <Spinner size="xs" />
          <Text fontSize="xs" color="var(--text-secondary-color)">
            {t("app.servers.specUrlChecking")}
          </Text>
        </HStack>
      )}

      {status.state === "valid" && (
        <HStack gap={2} color={{ base: "green.600", _dark: "green.300" }}>
          <MdCheckCircle />
          <Text fontSize="xs">{status.message}</Text>
        </HStack>
      )}

      {status.state === "invalid" && (
        <HStack gap={2} color={{ base: "red.600", _dark: "red.300" }}>
          <MdErrorOutline />
          <Text fontSize="xs" wordBreak="break-word">
            {status.message}
          </Text>
        </HStack>
      )}

      {status.state === "valid" && status.wasResolved && (
        <Text fontSize="xs" color="var(--text-secondary-color)">
          {t("app.servers.specUrlResolved")}
        </Text>
      )}
    </VStack>
  );
};

export default SpecUrlField;
